import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CropOverlay from '../components/CropOverlay';
import './Editor.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

function Editor() {
  const { logout } = useAuth();
  const [works, setWorks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const fileInputRef = useRef(null);

  const fetchWorks = useCallback(() => {
    fetch(`${API_BASE}/works.php?all=1`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => setWorks(data))
      .catch(err => console.error('Failed to load works:', err));
  }, []);

  useEffect(() => {
    fetchWorks();
  }, [fetchWorks]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${API_BASE}/upload.php`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    return res.json();
  };

  const handleFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    let lastUid = null;
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const data = await uploadFile(file);
        if (data.success) lastUid = data.uid;
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    fetchWorks();
    if (lastUid) {
      setSelected({
        uid: lastUid,
        title: '',
        medium: '',
        width: 0,
        height: 0,
        display: 0,
        priority: 0,
      });
    }
    setUploading(false);
  };

  const handleDropUpload = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles([...e.dataTransfer.files]);
  };

  const handleDragOverUpload = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeaveUpload = () => {
    setDragOver(false);
  };

  const handleFileInput = (e) => {
    handleFiles([...e.target.files]);
    e.target.value = '';
  };

  // Grid reorder drag handlers
  const handleGridDragStart = (e, index) => {
    setDragItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleGridDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItem(index);
  };

  const handleGridDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (dragItem === null || dragItem === dropIndex) {
      setDragItem(null);
      setDragOverItem(null);
      return;
    }

    const reordered = [...works];
    const [moved] = reordered.splice(dragItem, 1);
    reordered.splice(dropIndex, 0, moved);
    setWorks(reordered);
    setDragItem(null);
    setDragOverItem(null);

    // Save new priorities (highest priority first)
    const updates = reordered.map((work, i) => ({
      uid: work.uid,
      priority: reordered.length - i,
    }));

    try {
      await fetch(`${API_BASE}/works.php?reorder=1`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
    } catch (err) {
      console.error('Reorder failed:', err);
      fetchWorks();
    }
  };

  const handleGridDragEnd = () => {
    setDragItem(null);
    setDragOverItem(null);
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      await fetch(`${API_BASE}/works.php?id=${selected.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(selected),
      });
      fetchWorks();
    } catch (err) {
      console.error('Save failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm('Delete this artwork?')) return;
    try {
      await fetch(`${API_BASE}/works.php?id=${selected.uid}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setSelected(null);
      fetchWorks();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const updateField = (field, value) => {
    setSelected(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="editor">
      <div className="editor-header">
        <button className="btn-logout" onClick={logout}>Log Out</button>
      </div>
      <div className="editor-layout">
        <div className="editor-left">
          <div
            className={`editor-dropzone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
            onDrop={handleDropUpload}
            onDragOver={handleDragOverUpload}
            onDragLeave={handleDragLeaveUpload}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileInput}
              hidden
            />
            {uploading
              ? 'Uploading...'
              : 'Drop images here or click to browse'}
          </div>
          <div className="editor-grid">
          {works.map((work, index) => (
            <div
              key={work.uid}
              className={`editor-thumb ${selected?.uid === work.uid ? 'selected' : ''} ${work.display ? '' : 'hidden-work'} ${dragOverItem === index ? 'drag-target' : ''}`}
              draggable
              onDragStart={e => handleGridDragStart(e, index)}
              onDragOver={e => handleGridDragOver(e, index)}
              onDrop={e => handleGridDrop(e, index)}
              onDragEnd={handleGridDragEnd}
              onClick={() => setSelected(work)}
            >
              <img
                src={`${API_BASE}/../uploads/small/${work.uid}.png`}
                alt={work.title || 'Untitled'}
              />
              {!work.display && <span className="hidden-badge">Hidden</span>}
            </div>
          ))}
          </div>
        </div>

        {selected && (
          <div className="editor-panel">
            <div className="editor-preview">
              <CropOverlay
                key={selected.uid}
                imageUrl={`${API_BASE}/../uploads/full/${selected.uid}.png`}
                crop={{
                  x1: selected.crop_x1 ?? 0,
                  y1: selected.crop_y1 ?? 0,
                  x2: selected.crop_x2 ?? 1,
                  y2: selected.crop_y2 ?? 1,
                }}
                gridType={selected.grid_type || '1x1'}
                gamma={selected.gamma ?? 0}
                brightness={selected.brightness ?? 0}
                onChange={({ x1, y1, x2, y2 }) => {
                  setSelected(prev => ({
                    ...prev,
                    crop_x1: x1,
                    crop_y1: y1,
                    crop_x2: x2,
                    crop_y2: y2,
                  }));
                }}
              />
            </div>

            <div className="editor-fields">
              <label>
                Title
                <input
                  type="text"
                  value={selected.title}
                  onChange={e => updateField('title', e.target.value)}
                />
              </label>
              <label>
                Medium
                <input
                  type="text"
                  value={selected.medium}
                  onChange={e => updateField('medium', e.target.value)}
                  placeholder='e.g. "Oil on Canvas, 2014"'
                />
              </label>
              <div className="editor-row">
                <label>
                  Width (inches)
                  <input
                    type="number"
                    value={selected.width / 100 || ''}
                    onChange={e => updateField('width', Math.round(parseFloat(e.target.value || 0) * 100))}
                  />
                </label>
                <label>
                  Height (inches)
                  <input
                    type="number"
                    value={selected.height / 100 || ''}
                    onChange={e => updateField('height', Math.round(parseFloat(e.target.value || 0) * 100))}
                  />
                </label>
                <label>
                  Grid Size
                  <select
                    value={selected.grid_type || '1x1'}
                    onChange={e => updateField('grid_type', e.target.value)}
                  >
                    <option value="1x1">1×1</option>
                    <option value="2x1">2×1</option>
                    <option value="1x2">1×2</option>
                    <option value="2x2">2×2</option>
                  </select>
                </label>
              </div>
              <label className="editor-slider">
                Gamma ({(selected.gamma ?? 0).toFixed(2)})
                <input
                  type="range"
                  min="-1"
                  max="1"
                  step="0.01"
                  value={selected.gamma ?? 0}
                  onChange={e => updateField('gamma', parseFloat(e.target.value))}
                />
              </label>
              <label className="editor-slider">
                Brightness ({(selected.brightness ?? 0).toFixed(2)})
                <input
                  type="range"
                  min="-1"
                  max="1"
                  step="0.01"
                  value={selected.brightness ?? 0}
                  onChange={e => updateField('brightness', parseFloat(e.target.value))}
                />
              </label>
              <label className="editor-checkbox">
                <input
                  type="checkbox"
                  checked={!!selected.display}
                  onChange={e => updateField('display', e.target.checked ? 1 : 0)}
                />
                Display in gallery
              </label>
              <div className="editor-actions">
                <button className="btn-save" onClick={handleSave}>Save</button>
                <button className="btn-delete" onClick={handleDelete}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Editor;
